import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ArrowRightAlt, Flight, MoreVert } from '@mui/icons-material';
import {
  styled,
  useTheme,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material';

import { useGetItineraryById } from '../../queries';
import { returnAirlineLogoUrl } from '../../utils';

export const TimerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M15 1H9V3H15V1ZM11 14H13V8H11V14ZM19.03 7.39L20.45 5.97C20.02 5.46 19.55 4.98 19.04 4.56L17.62 5.98C16.07 4.74 14.12 4 12 4C7.03 4 3 8.03 3 13C3 17.97 7.02 22 12 22C16.98 22 21 17.97 21 13C21 10.88 20.26 8.93 19.03 7.39ZM12 20C8.13 20 5 16.87 5 13C5 9.13 8.13 6 12 6C15.87 6 19 9.13 19 13C19 16.87 15.87 20 12 20Z"
      fill="black"
      fillOpacity="0.87"
    />
  </svg>
);

const LegCard = styled(Card)`
  border-radius: 16px;
  width: 100%;
`;

const SegmentCard = styled(Card)`
  border: 1px solid ${({ theme }) => theme.palette.alphas.divider};
  border-radius: 16px;
  width: 100%;
`;

const Wheee = () => {
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ width: '8px', height: '8px', backgroundColor: theme.palette.paper.light, borderRadius: '50%', zIndex: 2 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
          <circle cx="4" cy="4" r="3" stroke="#4A148C" strokeWidth="2" />
        </svg>
      </Box>
      <Box flex={1}>
        <Divider
          orientation="vertical"
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto',
            borderLeftWidth: '1px',
            borderLeftRight: '1px',
            background: theme.palette.primaryAlternative.darker,
            zIndex: 1,
          }}
        />
      </Box>
      <Flight
        style={{
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          transform: 'rotate(180deg)',
        }}
      />
      <Box flex={1}>
        <Divider
          orientation="vertical"
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto',
            borderLeftWidth: '1px',
            borderLeftRight: '1px',
            background: theme.palette.primaryAlternative.darker,
            zIndex: 1,
          }}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{ width: '8px', height: '8px', backgroundColor: theme.palette.paper.light, borderRadius: '50%', zIndex: 2 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
          <circle cx="4" cy="4" r="3" stroke="#4A148C" strokeWidth="2" />
        </svg>
      </Box>
    </Box>
  );
};

export type FareSummaryProps = {
  title: string;
  fareId: number;
};

export const FareSummary = ({ title, fareId }: FareSummaryProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { data, isLoading } = useGetItineraryById(fareId);

  const fare = data?.data;

  return (
    <Box>
      {isLoading && (
        <LegCard>
          <CardHeader
            sx={{ borderBottom: `1px solid ${theme.palette.alphas.divider}` }}
            title={<Typography variant="body1">{title}</Typography>}
            action={
              <Box sx={{ paddingY: 1, paddingX: 2 }}>
                <IconButton aria-label="itinerary-options">
                  <MoreVert />
                </IconButton>
              </Box>
            }
          />
          <CardContent sx={{ paddingX: theme.spacing(1) }}>
            <Box
              display="flex"
              gap={2}
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              p={theme.spacing(10)}
            >
              <CircularProgress color="primary" />
              <Typography variant="body1" textAlign="center">
                {t('flights.availabilityLayout.loadingFlightsLegend')}
              </Typography>
            </Box>
          </CardContent>
        </LegCard>
      )}
      {!isLoading && !fare && (
        <LegCard>
          <CardHeader
            sx={{ borderBottom: `1px solid ${theme.palette.alphas.divider}` }}
            title={<Typography variant="body1">{title}</Typography>}
            action={
              <Box sx={{ padding: theme.spacing(1) }}>
                <IconButton aria-label="itinerary-options">
                  <MoreVert />
                </IconButton>
              </Box>
            }
          />
          <CardContent sx={{ paddingX: theme.spacing(1) }}>
            <Box
              display="flex"
              gap={2}
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              p={theme.spacing(10)}
            >
              <CircularProgress color="primary" />
              <Typography variant="body1" textAlign="center">
                {t('flights.availabilityLayout.noFareDataAvailable')}
              </Typography>
            </Box>
          </CardContent>
        </LegCard>
      )}
      {fare && (
        <LegCard>
          <CardHeader
            sx={{ borderBottom: `1px solid ${theme.palette.alphas.divider}` }}
            title={<Typography variant="body1">{title}</Typography>}
            action={
              <Box sx={{ padding: theme.spacing(1) }}>
                <IconButton aria-label="itinerary-options">
                  <MoreVert />
                </IconButton>
              </Box>
            }
          />
          <CardContent sx={{ paddingX: theme.spacing(1) }}>
            <List disablePadding sx={{ width: '100%' }}>
              {fare.legs.map((leg) => (
                <>
                  <ListItem
                    key={leg.id}
                    sx={{ paddingTop: (theme) => (leg.isReturn ? theme.spacing(2) : 0) }}
                    disablePadding
                  >
                    <Box display="flex" flexDirection="column">
                      <Box>
                        {leg.isReturn ? (
                          <Chip
                            avatar={
                              <Flight
                                style={{
                                  backgroundColor: 'transparent',
                                  color: theme.palette.primary.main,
                                  transform: 'rotate(270deg)',
                                }}
                              />
                            }
                            label={String(t('flights.availabilityLayout.isReturn')).toUpperCase()}
                            size="medium"
                            style={{
                              backgroundColor: theme.palette.field.main,
                              color: theme.palette.text.primary,
                            }}
                          />
                        ) : (
                          <Chip
                            avatar={
                              <Flight
                                style={{
                                  backgroundColor: 'transparent',
                                  color: theme.palette.primary.main,
                                  transform: 'rotate(90deg)',
                                }}
                              />
                            }
                            label={String(t('flights.availabilityLayout.isStart')).toUpperCase()}
                            size="medium"
                            style={{
                              backgroundColor: theme.palette.field.main,
                              color: theme.palette.text.primary,
                            }}
                          />
                        )}
                      </Box>
                      <Box my={theme.spacing(2)}>
                        <Box gap={1} display="flex" alignItems="center">
                          <Typography fontWeight={500} variant="subtitle1" color="text.primary">
                            {leg.departureAirport}
                          </Typography>
                          <ArrowRightAlt fontSize="small" />
                          <Typography fontWeight={500} variant="subtitle1" color="text.primary">
                            {leg.arrivalAirport}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" color="text.primary" fontWeight={500}>
                            {t('flights.availabilityLayout.totalDuration')}: {leg.durationLabel}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  <ListItem disablePadding>
                    <List disablePadding sx={{ width: '100%' }}>
                      {leg.segments.map((segment) => (
                        <ListItem key={segment.id} sx={{ paddingX: 0 }}>
                          <SegmentCard elevation={0}>
                            <CardContent>
                              <Box display="flex" gap={1}>
                                <Box
                                  flex="0 0 96px"
                                  display="flex"
                                  flexDirection="column"
                                  justifyContent="space-evenly"
                                >
                                  <Box display="flex" flexDirection="column" alignItems="flex-end">
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {dayjs(segment.departureTime).format('D-MMM YYYY')}
                                    </Typography>
                                    <Typography fontWeight={500} component="span" variant="body2" color="text.primary">
                                      {dayjs(segment.departureTime).format('HH:mm A')}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="flex-end">
                                    <TimerIcon />
                                    <Typography
                                      fontWeight={500}
                                      component="span"
                                      variant="body1"
                                      ml={theme.spacing(0.5)}
                                    >
                                      {segment.durationLabel}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" flexDirection="column" alignItems="flex-end">
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {dayjs(segment.arrivalTime).format('D-MMM YYYY')}
                                    </Typography>
                                    <Typography fontWeight={500} component="span" variant="body2" color="text.primary">
                                      {dayjs(segment.arrivalTime).format('HH:mm A')}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box display="flex" my={theme.spacing(2)}>
                                  <Wheee />
                                </Box>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  justifyContent="space-evenly"
                                  flexWrap="nowrap"
                                >
                                  <Box>
                                    <Typography fontWeight={500} variant="body2" component="p">
                                      {segment.departureAirportCityName}
                                      <Typography component="span" variant="body1" sx={{ mx: 1 }}>
                                        •
                                      </Typography>
                                      {segment.departureAirport}
                                    </Typography>
                                    <Typography variant="caption" component="p" color="text.secondary">
                                      {segment.departureAirportName}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <img
                                      style={{ maxHeight: 21, margin: '8px 0' }}
                                      src={returnAirlineLogoUrl(segment.operatingCarrier)}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" component="p" fontWeight={500}>
                                      {segment.arrivalAirportCityName}
                                      <Typography component="span" variant="body1" sx={{ mx: 1 }}>
                                        •
                                      </Typography>
                                      {segment.arrivalAirport}
                                    </Typography>
                                    <Typography variant="caption" component="p" color="text.secondary">
                                      {segment.arrivalAirportName}
                                    </Typography>
                                  </Box>
                                </Box>
                                {/* TODO: Poner todo lo de layover fuera de la card de segments */}
                                {segment.layoverTime && (
                                  <Typography variant="body2" component="p">
                                    Wait Time: {segment.layoverTime}
                                  </Typography>
                                )}
                                {segment.nightsOfStay && (
                                  <Typography variant="body2" component="p">
                                    Nights of Stay: {segment.nightsOfStay} in {segment.layoverCity}
                                  </Typography>
                                )}
                              </Box>
                            </CardContent>
                          </SegmentCard>
                        </ListItem>
                      ))}
                    </List>
                  </ListItem>
                </>
              ))}
            </List>
          </CardContent>
        </LegCard>
      )}
    </Box>
  );
};
